import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { redirectTo } from '@xing-com/crate-core-router';
import { ErrorState } from '@xing-com/empty-state';
import { GridContainer, GridItem } from '@xing-com/grid';
import { BREAKPOINTS, useMediaListener } from '@xing-com/util';

import { BrandBackground } from '../components/brand-background';
import { PageTitle } from '../components/page-title';
import { PerkList } from '../components/perk-list';
import type { ProductPerksByProduct } from '../components/perk-list';
import { ProductCards } from '../components/product-cards';
import { useActivationUrl } from '../hooks/use-activation-url';
import { useCardsCampaign } from '../hooks/use-cards-campaign';
import type { CardsCampaignOptions } from '../hooks/use-cards-campaign';
import { useFeatureLists } from '../hooks/use-feature-lists';
import type { UseFeatureLists } from '../hooks/use-feature-lists';
import { useNewDesignTracking } from '../hooks/use-new-design-tracking';
import { usePerkListDiscovery } from '../hooks/use-perk-list-discovery';
import { createProductCardsData } from '../utils/create-product-cards-data';
import { mapCampaignToCardData } from '../utils/map-campaign-to-card-data';

type NewDesignPageProps = {
  product: ProductType;
  campaignContext?: CardsCampaignOptions;
};

const createProductPerks = (
  featureLists: Pick<UseFeatureLists, 'groups' | 'hasCompleteData'>,
  formatMessage: IntlShape['formatMessage']
): ProductPerksByProduct | undefined => {
  const premiumGroups = featureLists.groups[ProductType.Premium];
  const proJobsGroups = featureLists.groups[ProductType.Projobs];

  if (!featureLists.hasCompleteData || !premiumGroups || !proJobsGroups) {
    return undefined;
  }

  return {
    [ProductType.Premium]: {
      intro: formatMessage({
        id: 'UPSELL_PURCHASE_PERK_LIST_PREMIUM_INTRO',
      }),
      groups: premiumGroups,
    },
    [ProductType.Projobs]: {
      intro: formatMessage({
        id: 'UPSELL_PURCHASE_PERK_LIST_PROJOBS_INTRO',
      }),
      groups: proJobsGroups,
    },
  };
};

export const NewDesignPage: React.FC<NewDesignPageProps> = ({
  product,
  campaignContext,
}) => {
  const { campaigns, errors, loading, retry } = useCardsCampaign(
    product,
    campaignContext
  );
  const { formatMessage, formatNumber } = useIntl();
  const featureLists = useFeatureLists();
  const ownedProducts = {
    [ProductType.Premium]: campaigns?.premium.status === 'owned',
    [ProductType.Projobs]: campaigns?.projobs.status === 'owned',
  };
  const cardData = createProductCardsData(
    product,
    {
      premium: mapCampaignToCardData(
        ProductType.Premium,
        campaigns?.premium.campaign ?? null
      ),
      projobs: mapCampaignToCardData(
        ProductType.Projobs,
        campaigns?.projobs.campaign ?? null
      ),
    },
    { formatMessage, formatNumber },
    ownedProducts
  );
  const activationUrls = useActivationUrl();
  const matchedMedia = useMediaListener();
  const isSmallScreen = matchedMedia === BREAKPOINTS.handheld;
  const { discoverPerks, perkListKey, perkListProduct, perkListRef } =
    usePerkListDiscovery(product);
  const productPerks = createProductPerks(featureLists, formatMessage);
  const [productCardsGroupRef, isProductCardsGroupInView] = useInView({
    fallbackInView: true,
    initialInView: true,
  });

  const routeCampaign =
    product === ProductType.Premium ? campaigns?.premium : campaigns?.projobs;
  const routeCampaignError =
    product === ProductType.Premium ? errors?.premium : errors?.projobs;
  const hasCampaignError =
    !loading &&
    (routeCampaignError !== undefined ||
      routeCampaign?.status === 'error' ||
      routeCampaign?.status === 'unavailable' ||
      cardData === null);
  const { trackActivation, trackFeatureDiscovery, trackFeatureTab } =
    useNewDesignTracking({
      hasCampaignError,
      product,
      routeCampaign:
        routeCampaign?.status === 'available' ? routeCampaign.campaign : null,
    });

  // Redirect only when the membership feature confirms that a missing offer
  // represents an existing membership.
  if (!loading && routeCampaign?.status === 'owned') {
    redirectTo(`/${product}/features`);
    return null;
  }

  if (hasCampaignError) {
    return (
      <ErrorState
        buttonProps={{
          onClick: () => {
            void retry().catch(() => undefined);
          },
        }}
        size="large"
      />
    );
  }

  return (
    <>
      <BrandBackground />
      <GridContainer variant="wide">
        <GridItem>
          <PageTitle />
        </GridItem>
        <GridItem>
          <ProductCards
            productCardsGroupRef={productCardsGroupRef}
            activationUrls={activationUrls}
            data={cardData}
            loading={loading}
            onDiscover={discoverPerks}
            onActivate={trackActivation}
            onFeatureDiscovery={trackFeatureDiscovery}
            perks={productPerks}
          />
        </GridItem>
        {!isSmallScreen ? (
          <GridItem span={12} startLarge={3} spanLarge={8}>
            <PerkList
              key={perkListKey}
              ref={perkListRef}
              activationUrls={activationUrls}
              initialProduct={perkListProduct}
              isLoading={loading || featureLists.loading}
              onActivate={trackActivation}
              onFeatureTab={trackFeatureTab}
              ownedProducts={ownedProducts}
              perks={productPerks}
              isProductCardsGroupInView={isProductCardsGroupInView}
            />
          </GridItem>
        ) : null}
      </GridContainer>
    </>
  );
};
